import {
  ActivityBlock,
  ActivityGlyph,
  AssistantProse,
  AttachmentCard,
  Composer,
  ConversationColumn,
  ConversationTurn,
  MessageAttachmentRow,
  UserMessage,
  agentLifecycleRow,
} from '~/design-system/components/v1_5/Chat';
import { QuestionCard, type QuestionData } from '~/deck15/chat/question';
import { DeckMark } from '~/design-system/components/v1_5/Header/DeckMark';
import { PanelHeader } from '~/design-system/components/v1_5/Layout';
import { PreviewPane } from '~/design-system/components/v1_5/Preview';
import { cn } from '~/lib/utils';
import { SlideArt, SlideThumb, type SlideKind } from './SlideArt';
import { useBeat, useInView, usePrefersReducedMotion } from './useDemoBeat';
import styles from './AgentAsksDemo.module.css';

/**
 * Feature row 1 — "The agent that asks before it acts" (Guri, 2026-09-04).
 *
 * The hero plays the whole seed-deck run; this plays only the beat the row is about. You arrive
 * mid-conversation: the brief is in, the files are read, and instead of building the agent has
 * stopped to ask one question. Picking a chip writes the answer into the composer the way the app
 * does, and the next step starts in front of you — the outline, the artifacts landing in the
 * directory, the first one open on the stage.
 *
 * The real Deck 1.5 parts, not mocks: the same `ConversationColumn`, `ActivityBlock` question and
 * `PreviewPane` the app ships, so the row is the product rather than a drawing of it. Unlike the
 * other rows' skeleton this one fills its panel instead of bleeding off it, because all three panes
 * have to be visible for the flow to read (Guri, 2026-09-04: "also show directory and preview").
 */

const THEME = 'dark' as const;
const MODEL = { vendor: 'google', label: 'Gemini 3.8 Flash' } as const;
/**
 * The two it asks, on the wizard route — which is the card that stands in the composer's slot
 * rather than the inline chip, because there is more than one of them.
 */
const QUESTIONS: QuestionData[] = [
  {
    id: 'depth',
    question: 'Do you want the process in it, or only the final screens?',
    type: 'single',
    options: [
      { id: 'process', label: 'Show the process' },
      { id: 'finals', label: 'Only the finals' },
    ],
  },
  {
    id: 'length',
    question: 'How long have they got?',
    type: 'single',
    options: [
      { id: 'five', label: 'Five minutes' },
      { id: 'fifteen', label: 'Fifteen minutes' },
      { id: 'later', label: 'They will read it later' },
    ],
  },
];

/** The beat order. The conversation is already under way at the first one. */
const BEATS = ['asking', 'picked', 'second', 'answered', 'building', 'built'] as const;
type Beat = (typeof BEATS)[number];

const DWELL: Record<Beat, number> = {
  asking: 2600,
  picked: 900,
  second: 2400,
  answered: 900,
  building: 2200,
  built: 4200,
};

/**
 * The eight the outline names, in the order they land in the directory. The thumbnails are real
 * rendered slides rather than wireframes (Guri, 2026-09-04), so the list reads as a deck someone
 * actually has open.
 */
const OUTLINE: readonly { title: string; kind: SlideKind }[] = [
  { title: 'Three directions', kind: 'trio' },
  { title: 'Where we started', kind: 'metrics' },
  { title: 'What testing said', kind: 'words' },
  { title: 'The final flow', kind: 'flow' },
  { title: 'What changed', kind: 'pair' },
  { title: 'Before and after', kind: 'mosaic' },
  { title: 'What it cost', kind: 'figure' },
  { title: 'What is next', kind: 'plan' },
];

export interface AgentAsksDemoProps {
  className?: string;
}

export function AgentAsksDemo({ className }: AgentAsksDemoProps) {
  const [hostRef, inView] = useInView<HTMLDivElement>();
  const reduceMotion = usePrefersReducedMotion();

  const beat = useBeat(BEATS, DWELL, inView && !reduceMotion);
  const at = (name: Beat) => BEATS.indexOf(beat) >= BEATS.indexOf(name);
  const writing = beat === 'building';

  /** The card is controlled, so the beat is what fills it in. */
  const answers = {
    ...(at('picked') ? { depth: ['Show the process'] } : {}),
    ...(at('answered') ? { length: ['Fifteen minutes'] } : {}),
  };

  return (
    <div ref={hostRef} className={cn(styles.window, className)} aria-hidden>
      <div className={styles.bar}>
        <DeckMark theme={THEME} hover="inert" />
        <span className={styles.barTitle}>Deck, Product v2 case</span>
      </div>
      <div className={styles.panes}>
      <div className={styles.agent}>
        <PanelHeader theme={THEME}>Agent</PanelHeader>
        <div className={styles.thread}>
          <ConversationColumn theme={THEME}>
            {/* No opening brief: you arrive part way in, and what came before is clipped off the
                top of the column the way a scrolled thread is. */}
            <ConversationTurn theme={THEME} spaced={false}>
              <AssistantProse theme={THEME}>
                Both are in. The frames are the June set, and the notes cover two rounds of testing.
              </AssistantProse>
              <ActivityBlock
                theme={THEME}
                row={{
                  density: 'collapsed',
                  disclosure: 'down',
                  icon: <ActivityGlyph state="done" size={15} tone="hint" theme={THEME} />,
                  label: 'Read 2 sources',
                  meta: 'Product v2 · Figma, testing-notes.md',
                  theme: THEME,
                  onToggle: () => {},
                }}
              />
              {/* The row that says it stopped to ask. The questions themselves are on the card
                  below, in the composer's slot. */}
              <ActivityBlock
                theme={THEME}
                row={{
                  density: 'open',
                  icon: <ActivityGlyph tool="chat" size={16} theme={THEME} />,
                  label: 'A few things first',
                  theme: THEME,
                }}
              />
            </ConversationTurn>

            {/* Picking a chip IS the answer. It never gets echoed back as a message; the agent
                simply carries on from it. */}
            {at('building') ? (
              <ConversationTurn theme={THEME}>
                <AssistantProse theme={THEME}>
                  Ok. The three directions go in, then the final flow, then what testing changed.
                </AssistantProse>
                <ActivityBlock
                  theme={THEME}
                  row={agentLifecycleRow({
                    lifecycle: at('built') ? 'complete' : 'running',
                    label: at('built') ? 'Built the outline' : 'Building the outline',
                    meta: at('built') ? '8 artifacts' : '5 of 8',
                    onToggle: () => {},
                    theme: THEME,
                  })}
                />
              </ConversationTurn>
            ) : null}
          </ConversationColumn>
        </div>
        {/* The takeover: while it is asking, the card IS the composer's slot. The two are never up
            together (Guri, 2026-09-04: "that chatbar transforms into that question wizard"). */}
        <div className={styles.dock}>
          {at('building') ? (
            <Composer
              theme={THEME}
              input={{ content: 'placeholder', text: 'Share your work context…' }}
              toolbar={{ model: MODEL, send: { state: writing ? 'stop' : 'idle' } }}
            />
          ) : (
            <QuestionCard
              theme={THEME}
              questions={QUESTIONS}
              answers={answers}
              currentIndex={at('second') ? 1 : 0}
              onChange={() => {}}
              onIndexChange={() => {}}
              onSubmit={() => {}}
              onSkipAll={() => {}}
            />
          )}
        </div>
      </div>

      <div className={styles.directory}>
        <PanelHeader theme={THEME}>Artifacts</PanelHeader>
        <div className={styles.list}>
          {/* The deck already has its eight. They are never absent; the run at the end rewrites
              them, resolving one after another (Guri, 2026-09-04). */}
          {OUTLINE.map((artifact, i) => (
            <div
              key={artifact.title}
              className={cn(styles.item, i === 0 && styles.itemOn, writing && styles.itemWriting)}
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              <SlideThumb kind={artifact.kind} />
              <span className={styles.lines}>
                <span className={styles.itemTitle}>{artifact.title}</span>
                <span className={styles.itemMeta}>
                  {writing ? 'writing' : `artifact ${String(i + 1).padStart(2, '0')}`}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.preview}>
        <PreviewPane
          theme={THEME}
          title={OUTLINE[0].title}
          meta={writing ? 'artifact 01 · writing' : 'artifact 01'}
          status={writing ? 'writing' : 'ready'}
          percent={writing ? 62 : undefined}
          kind="slide"
          naturalWidth={1280}
          naturalHeight={720}
          indicator="1 of 8"
          showZoom={false}
        >
          <SlideArt kind={OUTLINE[0].kind} />
        </PreviewPane>
      </div>
      </div>
    </div>
  );
}

AgentAsksDemo.displayName = 'AgentAsksDemo';
